/**
 * L2 드리프트 결과 로더 — "이 문서가 설명하는 코드가 그 뒤로 바뀌었는가".
 *
 * 정본은 `scripts/manual/drift.mjs`가 git 이력으로 계산해 남기는 `docs/manual/_generated/stale.json`.
 *
 * ⚠️ **그 파일은 없을 수 있다.** gitignore 대상이라 스크립트를 한 번도 안 돌린 로컬·CI 체크아웃에는
 * 존재하지 않는다. 드리프트 표시는 어디까지나 부가 정보이므로, 없으면 **조용히 빈 결과**로 동작한다
 * (매뉴얼 자체가 안 열리는 일이 없어야 한다). 그래서 정적 import가 아니라 `import.meta.glob`이다 —
 * 정적 import는 파일이 없으면 빌드가 깨진다.
 */

/** 기준 시각 이후 바뀐 covers 파일 하나. */
export interface ManualStaleFile {
  path: string;
  /** 기준 시각 이후의 커밋 수 — 이 값이 클수록 문서가 크게 어긋나 있다. */
  commits: number;
  lastChangedAt: string;
}

/** 낡은 문서 하나. */
export interface ManualStaleEntry {
  path: string;
  manual: string;
  section: string;
  slug: string;
  title: string;
  /** 판정 기준 시각(ISO). */
  baseline: string;
  /** 기준 표시용 날짜(`YYYY-MM-DD`). */
  baselineLabel: string;
  baselineSource: 'reviewedAt' | 'commit';
  files: ManualStaleFile[];
  totalCommits: number;
  lastChangedAt: string;
  /** 생성물(`generated: true`) 문서면 true — 처방이 「재생성 1회」다. 손집필에는 키가 없다. */
  generated?: true;
}

interface ManualStaleReport {
  generatedAt?: string;
  stale?: ManualStaleEntry[];
  /**
   * `generated: true` 문서의 낡음 — 2026-10-05에 `stale`에서 갈라낸 구획.
   *
   * ⚠️ **화면 뱃지는 둘을 합쳐서 본다.** 보고서를 가른 이유는 *처방*이 다르기 때문이고
   * (읽고 판정 ↔ 재생성 1회), 독자에게 「이 문서가 설명하는 코드가 그 뒤로 바뀌었다」는
   * 사실은 **양쪽 다 참**이다. 합치지 않으면 생성물 4편의 뱃지가 조용히 사라진다.
   * 화면에서 둘을 다르게 보일지는 별건 — 그때 `entry.generated`로 가른다.
   */
  generated?: ManualStaleEntry[];
}

const EMPTY: ManualStaleEntry[] = [];

/**
 * `apps/admin/src/pages/manual/` → 레포 루트까지 5단계 (manualContent.ts와 같은 기준).
 * 와일드카드로 받아 키로 골라낸다 — 파일이 없어도 빈 객체가 될 뿐 빌드가 깨지지 않는다.
 */
const rawReports = import.meta.glob('../../../../../docs/manual/_generated/*.json', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

/**
 * 보고서의 두 구획을 한 목록으로 — **순수 함수**(테스트 대상).
 *
 * 합치는 이유는 `ManualStaleReport.generated` 주석에 있다. 가르는 것은 소비처의 몫이고,
 * 그 판정축은 `entry.generated` 하나다.
 */
export function mergeStaleReport(parsed: ManualStaleReport | null | undefined): ManualStaleEntry[] {
  const handwritten = Array.isArray(parsed?.stale) ? parsed.stale : EMPTY;
  const fromGenerator = Array.isArray(parsed?.generated) ? parsed.generated : EMPTY;
  if (fromGenerator.length === 0) return handwritten;
  return [...handwritten, ...fromGenerator];
}

/** 매뉴얼별 건수 — 두 구획이 **같은 방법으로** 세도록 집계는 한 벌만 둔다. */
function countByManual(
  entries: ManualStaleEntry[],
  keep: (entry: ManualStaleEntry) => boolean,
): Record<string, number> {
  return entries.reduce<Record<string, number>>((acc, entry) => {
    if (!keep(entry)) return acc;
    acc[entry.manual] = (acc[entry.manual] ?? 0) + 1;
    return acc;
  }, {});
}

/**
 * 매뉴얼별 **사람이 할 일**의 수 — 생성물은 세지 않는다(순수 함수).
 *
 * ⚠️ 「정비 필요 N건」은 *읽고 판정할 문서의 수*다. 생성물은 처방이 `pnpm manual:reference`이고
 * 내용이 정확해도 경고가 남으므로(생성기가 `reviewedAt`을 쓰면서 그 줄을 내용 비교에서 뺀다)
 * 세면 **0이 될 수 없는 수**가 된다. `scripts/manual/drift.mjs`의 「정비 필요 N편 — 손집필」과
 * 같은 뜻으로 맞춘 것이다 — 두 숫자가 갈리면 어느 쪽이 맞는지 아무도 모른다.
 */
export function countReviewNeededByManual(entries: ManualStaleEntry[]): Record<string, number> {
  return countByManual(entries, (entry) => !entry.generated);
}

/**
 * 매뉴얼별 **재생성 대기**(생성물 낡음)의 수 — 위와 정확히 반대 구획(순수 함수).
 *
 * 둘을 같은 집계로 만든 이유는 **비대칭이 드리프트의 입구**이기 때문이다. 종전에는 「정비 필요」만
 * 사전 계산 맵이고 재생성 수는 소비처에서 매번 `filter`였는데(성능이 아니라 **판정이 두 벌**인 것이
 * 문제다), 한쪽의 제외 기준만 바뀌면 두 수의 합이 조용히 전체와 어긋난다.
 */
export function countGeneratedByManual(entries: ManualStaleEntry[]): Record<string, number> {
  return countByManual(entries, (entry) => !!entry.generated);
}

function loadStale(): ManualStaleEntry[] {
  const entry = Object.entries(rawReports).find(([path]) => path.endsWith('/stale.json'));
  const raw = entry?.[1];
  if (!raw) return EMPTY;
  try {
    return mergeStaleReport(JSON.parse(raw) as ManualStaleReport);
  } catch {
    // 깨진 산출물 때문에 매뉴얼이 안 열리면 본말전도다.
    console.warn('[manual] stale.json을 읽지 못했습니다 — 드리프트 표시를 생략합니다.');
    return EMPTY;
  }
}

/** 낡은 문서 전체 — 손집필 + 생성물 (스크립트 미실행 시 빈 배열). */
export const staleEntries: ManualStaleEntry[] = loadStale();

const byKey = new Map<string, ManualStaleEntry>(
  staleEntries.map((entry) => [`${entry.manual}/${entry.section}/${entry.slug}`, entry]),
);

const reviewNeededByManual = countReviewNeededByManual(staleEntries);
const generatedByManual = countGeneratedByManual(staleEntries);

/**
 * 이 문서가 낡았으면 그 내역, 아니면 `undefined`. **생성물도 돌려준다** —
 * 「이 문서가 설명하는 코드가 그 뒤로 바뀌었다」는 독자에게 양쪽 다 참이다.
 * 「정비할 것」을 세는 자리는 `getStale`이 아니라 `needsReview`를 쓸 것.
 */
export function getStale(
  manual: string,
  section: string,
  slug: string,
): ManualStaleEntry | undefined {
  return byKey.get(`${manual}/${section}/${slug}`);
}

/** 사람이 읽고 판정해야 하는 문서인가 — 목록의 정비 표시가 쓰는 판정(생성물 제외). */
export function needsReview(manual: string, section: string, slug: string): boolean {
  const entry = getStale(manual, section, slug);
  return entry !== undefined && !entry.generated;
}

/** 한 매뉴얼의 정비 필요 문서 수 (0이면 표시하지 않는다). */
export function staleCountOf(manual: string): number {
  return reviewNeededByManual[manual] ?? 0;
}

/** 한 매뉴얼의 생성물 낡음 수 — 재생성 대기. 「정비 필요」와 **별도로** 보여 준다. */
export function regenerateCountOf(manual: string): number {
  return generatedByManual[manual] ?? 0;
}
